import { z } from 'zod';
import type { SynergyEffect } from '@prism/sim';
import { DamageKindSchema, StatusKindSchema, ThresholdSchema } from './common.js';

/**
 * Runtime validation for the `SynergyEffect` union (TRD-08 §4).
 *
 * Effects are DATA, never functions or code strings (TRD-08 R-08-07). The server
 * re-simulates submitted runs, so the moment one synergy becomes a callback, content
 * stops being serializable and replay validation dies. This schema is what enforces that
 * at the boundary — a content author cannot smuggle in behaviour the server cannot run.
 *
 * The union is recursive (`everyNthShot.then`, `chanceOnKill.then`, `periodic.then`), so
 * it needs `z.lazy` and an explicit type annotation to break the inference cycle.
 */

const NumericTowerModSchema = z.enum([
  'flatDamage',
  'damageMultiplier',
  'attackSpeedMultiplier',
  'rangeBonus',
  'rangeMultiplier',
  'critChance',
  'critMultiplier',
  'armorPierce',
  'percentMaxHpDamage',
  'chainCount',
  'pierceCount',
]);

const NumericRunModSchema = z.enum([
  'goldPerWave',
  'interestCap',
  'rerollCostMultiplier',
  'bonusPlacements',
  'bonusCoreHp',
  'goldDropChance',
  'leakDamageOverride',
]);

const EffectTargetSchema = z.discriminatedUnion('s', [
  z.object({ s: z.literal('sameClass') }),
  z.object({ s: z.literal('allTowers') }),
  z.object({ s: z.literal('adjacent'), radiusTiles: z.number().positive() }),
]);

export const SynergyEffectSchema: z.ZodType<SynergyEffect> = z.lazy(() =>
  z.discriminatedUnion('k', [
    z.object({
      k: z.literal('stat'),
      target: EffectTargetSchema,
      stat: NumericTowerModSchema,
      op: z.enum(['add', 'mul']),
      value: z.number(),
    }),
    z.object({
      k: z.literal('runStat'),
      stat: NumericRunModSchema,
      op: z.enum(['add', 'mul', 'set']),
      value: z.number(),
    }),
    z.object({
      // A one-time grant before the first build phase, not a per-tick mod — see the
      // effect's comment in the sim. Authoring it without this entry typechecks and then
      // fails validation at runtime, which is how it would go unnoticed twice.
      k: z.literal('startingGold'),
      amount: z.number().int(),
    }),
    z.object({
      k: z.literal('applyStatus'),
      on: z.literal('hit'),
      status: StatusKindSchema,
      magnitude: z.number(),
      durationTicks: z.number().int().positive(),
      stacks: z.number().int().positive().optional(),
    }),
    z.object({
      k: z.literal('everyNthShot'),
      n: z.number().int().min(2),
      then: z.array(SynergyEffectSchema).min(1),
    }),
    z.object({
      k: z.literal('chanceOnKill'),
      p: z.number().gt(0).max(1),
      then: z.array(SynergyEffectSchema).min(1),
    }),
    z.object({
      k: z.literal('onKillExplode'),
      radiusTiles: z.number().positive(),
      percentMaxHp: z.number().gt(0),
      kind: DamageKindSchema,
      requireStatus: StatusKindSchema.optional(),
    }),
    z.object({
      k: z.literal('onKillSpreadStatus'),
      status: StatusKindSchema,
      radiusTiles: z.number().positive(),
      stacks: z.number().int().positive(),
      requireStacks: z.number().int().positive().optional(),
    }),
    z.object({ k: z.literal('executeBelow'), percentMaxHp: z.number().gt(0).lt(1) }),
    z.object({
      k: z.literal('bonusVsStatus'),
      status: StatusKindSchema,
      damageMultiplier: z.number().positive(),
    }),
    z.object({
      k: z.literal('shotModifier'),
      pierceAdd: z.number().int().nonnegative().optional(),
      chainAdd: z.number().int().nonnegative().optional(),
      damageMultiplier: z.number().positive().optional(),
      kindOverride: DamageKindSchema.optional(),
    }),
    z.object({
      k: z.literal('statusTuning'),
      status: StatusKindSchema,
      capOverride: z.number().int().positive().optional(),
      tickIntervalTicks: z.number().int().positive().optional(),
      /** The only lever Chill has — it expires rather than ticking. */
      durationMultiplier: z.number().positive().optional(),
    }),
    z.object({
      k: z.literal('periodic'),
      everyTicks: z.number().int().positive(),
      then: z.array(SynergyEffectSchema).min(1),
    }),
    z.object({
      k: z.literal('fieldStatus'),
      status: StatusKindSchema,
      durationTicks: z.number().int().positive(),
      magnitude: z.number(),
    }),
    z.object({
      k: z.literal('aura'),
      radiusTiles: z.number().positive(),
      radiusFromRange: z.boolean().optional(),
      stat: NumericTowerModSchema,
      op: z.enum(['add', 'mul']),
      value: z.number(),
    }),
    z.object({
      k: z.literal('auraStatus'),
      radiusTiles: z.number().positive(),
      status: StatusKindSchema,
      magnitude: z.number(),
      durationTicks: z.number().int().positive(),
    }),
    z.object({
      k: z.literal('summon'),
      everyTicks: z.number().int().positive(),
      flatDamage: z.number().nonnegative(),
      damageFromOwnerPercent: z.number().nonnegative(),
      lifetimeTicks: z.number().int().positive(),
    }),
    z.object({
      k: z.literal('summonExpiryExplode'),
      radiusTiles: z.number().positive(),
      damage: z.number().positive(),
    }),
    z.object({
      k: z.literal('gold'),
      amount: z.number().positive(),
      trigger: z.enum(['waveClear', 'kill']),
      chance: z.number().gt(0).max(1).optional(),
    }),
    z.object({
      k: z.literal('shopClassRestriction'),
      classCount: z.number().int().min(1).max(10),
    }),
    z.object({ k: z.literal('thresholdShift'), delta: z.number().int() }),
  ]),
);

export const ClassTierSchema = z.object({
  threshold: ThresholdSchema,
  /** Rendered in the synergy panel — must fit one line (TRD-09 R-09-18). */
  summary: z.string().min(4).max(140),
  effects: z.array(SynergyEffectSchema).min(1),
});
